import type { UserRole } from './auth.js';
import type { PublicAsset, PublicShipment, PublicTote, PublicTrailer } from './catalog.js';

/** Event / loadout types (F-04, ADR-008, DATA-MODEL.md). */

export const EVENT_STATUSES = ['draft', 'locked', 'scratch', 'completed'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Draft',
  locked: 'Locked',
  scratch: 'Scratch',
  completed: 'Completed',
};

export const EVENT_FORMATS = ['individual', 'duals', 'both'] as const;
export type EventFormat = (typeof EVENT_FORMATS)[number];

export const EVENT_FORMAT_LABELS: Record<EventFormat, string> = {
  individual: 'Individual',
  duals: 'Duals',
  both: 'Both',
};

export const EVENT_LEVELS = ['youth', 'high_school', 'college', 'other'] as const;
export type EventLevel = (typeof EVENT_LEVELS)[number];

export const EVENT_LEVEL_LABELS: Record<EventLevel, string> = {
  youth: 'Youth',
  high_school: 'High School',
  college: 'College',
  other: 'Other',
};

export const STAFF_ROLE_LABELS = ['lead', 'crew'] as const;
export type StaffRoleLabel = (typeof STAFF_ROLE_LABELS)[number];

export const CONFLICTS_POLICIES = ['warn', 'block'] as const;
export type ConflictsPolicy = (typeof CONFLICTS_POLICIES)[number];

export const ASSIGNMENT_CONFLICT_KINDS = [
  'staff',
  'tote',
  'trailer',
  'shipment',
  'asset',
  'availability',
] as const;
export type AssignmentConflictKind = (typeof ASSIGNMENT_CONFLICT_KINDS)[number];

export const DEFAULT_BRACKET_PLATFORM = 'floarena';
export const DEFAULT_STREAMING_PLATFORM = 'none';

export function isEventStatus(value: unknown): value is EventStatus {
  return typeof value === 'string' && (EVENT_STATUSES as readonly string[]).includes(value);
}

export function isEventFormat(value: unknown): value is EventFormat {
  return typeof value === 'string' && (EVENT_FORMATS as readonly string[]).includes(value);
}

export function isEventLevel(value: unknown): value is EventLevel {
  return typeof value === 'string' && (EVENT_LEVELS as readonly string[]).includes(value);
}

/** Youth, High School, College, Other. Duplicates collapse. Click order is ignored. */
export function orderedEventLevels(values: readonly EventLevel[]): EventLevel[] {
  const selected = new Set(values);
  return EVENT_LEVELS.filter((level) => selected.has(level));
}

export function isStaffRoleLabel(value: unknown): value is StaffRoleLabel {
  return typeof value === 'string' && (STAFF_ROLE_LABELS as readonly string[]).includes(value);
}

export function isConflictsPolicy(value: unknown): value is ConflictsPolicy {
  return typeof value === 'string' && (CONFLICTS_POLICIES as readonly string[]).includes(value);
}

/** Admin, equipment admin, and staff create and edit events. Viewers and TD codes do not. */
export function roleCanEditEvents(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin' || role === 'staff';
}

export interface DateSpan {
  start: string;
  end: string;
}

export interface LookupOption {
  slug: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

export interface PublicOrganization {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicEvent {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  confirmed: boolean;
  status: EventStatus;
  organizationId: string | null;
  organizationName: string | null;
  locationText: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contractText: string | null;
  matsRequested: number | null;
  bracketPlatform: string;
  streamingPlatform: string;
  format: EventFormat | null;
  /** Zero or more levels, in Youth, High School, College, Other order. */
  levels: EventLevel[];
  equipmentNeedsText: string | null;
  priority: number | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  loadoutId: string;
  /** Assigned loadout staff names only — never emails (F-12). */
  staffNames: string[];
  /** Assigned EWP trailer and shipment names (F-13). */
  shippedVia: string[];
  /** Admin-only Zoho Books fields (F-17). Omitted for every other role. */
  quotedPriceCents?: number | null;
  zohoEstimateId?: string | null;
  zohoEstimateNumber?: string | null;
  zohoInvoiceId?: string | null;
  zohoInvoiceNumber?: string | null;
  zohoInvoiceStatus?: string | null;
}

export interface PublicLoadoutStaff {
  userId: string;
  name: string;
  email: string;
  role: UserRole;
  roleLabel: StaffRoleLabel | null;
}

export interface AssignmentConflict {
  kind: AssignmentConflictKind;
  resourceId: string;
  resourceLabel: string;
  otherLoadoutId: string;
  otherEventIds: string[];
  otherEventNames: string[];
}

export interface PublicLoadout {
  id: string;
  name: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  span: DateSpan | null;
  events: PublicEvent[];
  staff: PublicLoadoutStaff[];
  /** Derived from Shipped Via packing (F-13). */
  totes: PublicTote[];
  trailers: PublicTrailer[];
  shipments: PublicShipment[];
  /** Derived from Shipped Via packing (F-13). */
  assets: PublicAsset[];
  conflicts: AssignmentConflict[];
  conflictsPolicy: ConflictsPolicy;
}

export interface EventListResponse {
  events: PublicEvent[];
  /** Next 7 America/Chicago calendar dates (today through today+6). */
  window: DateSpan;
  /** Same span as `window`. Kept so existing `view=weekend` clients keep working. */
  weekend: DateSpan;
  view: 'weekend' | 'all';
}

export interface EventResponse {
  event: PublicEvent;
  loadout: PublicLoadout;
}

export interface LoadoutResponse {
  loadout: PublicLoadout;
}

export interface OrganizationListResponse {
  organizations: PublicOrganization[];
}

export interface OrganizationResponse {
  organization: PublicOrganization;
}

export interface EventOptionsResponse {
  organizations: PublicOrganization[];
  bracketPlatforms: LookupOption[];
  streamingPlatforms: LookupOption[];
  users: PublicLoadoutStaff[];
  totes: PublicTote[];
  trailers: PublicTrailer[];
  shipments: PublicShipment[];
  assets: PublicAsset[];
  conflictsPolicy: ConflictsPolicy;
  window: DateSpan;
  weekend: DateSpan;
}

export interface LookupListResponse {
  options: LookupOption[];
}

export interface LookupResponse {
  option: LookupOption;
}

export interface ConflictCheckResponse {
  conflicts: AssignmentConflict[];
  conflictsPolicy: ConflictsPolicy;
}

export interface PackingWriteResponse {
  packedTotes: PublicTote[];
  packedAssets: PublicAsset[];
  conflicts: AssignmentConflict[];
  conflictsPolicy: ConflictsPolicy;
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Calendar date as `Sat 12 Sep` (F-12 list/summary meta). */
export function formatEventCalendarDate(ymd: string): string {
  const parts = ymd.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (year === undefined || month === undefined || day === undefined) return ymd;
  const utc = new Date(Date.UTC(year, month - 1, day));
  const weekday = WEEKDAY_SHORT[utc.getUTCDay()];
  const monthLabel = MONTH_SHORT[month - 1];
  if (!weekday || !monthLabel) return ymd;
  return `${weekday} ${day} ${monthLabel}`;
}

export function formatEventMetaLine(event: {
  startDate: string;
  endDate: string;
  locationText: string | null;
}): string {
  const dates =
    event.endDate !== event.startDate
      ? `${formatEventCalendarDate(event.startDate)} – ${formatEventCalendarDate(event.endDate)}`
      : formatEventCalendarDate(event.startDate);
  const location = event.locationText?.trim();
  return location ? `${dates} · ${location}` : dates;
}

export function staffSummaryLine(names: readonly string[] | undefined): string | null {
  const cleaned = (names ?? []).map((name) => name.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  return `Staff: ${cleaned.join(', ')}`;
}

export function tournamentDirectorLine(event: {
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
}): string | null {
  const parts = [event.contactName, event.contactEmail, event.contactPhone]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean);
  if (parts.length === 0) return null;
  return `Tournament director: ${parts.join(' · ')}`;
}

export function shippedViaLine(names: readonly string[] | undefined): string | null {
  const cleaned = (names ?? []).map((name) => name.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  return `Shipped Via: ${cleaned.join(', ')}`;
}
